import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import type { SearchCategory } from '../store/authStore';
import api from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import Header from '../components/Header';
import BackToDashboard from '../components/BackToDashboard';
import '../App.css';

type UserType = 'individual' | 'freelancer' | 'small_company';

const SEARCH_COUNTRIES: { code: string; name: string }[] = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'IE', name: 'Ireland' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'IN', name: 'India' },
  { code: 'SG', name: 'Singapore' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
];

const SEARCH_CATEGORIES: { id: string; label: string }[] = [
  { id: 'medical', label: 'Medical' },
  { id: 'real_estate', label: 'Real Estate' },
  { id: 'restaurant', label: 'Restaurants' },
  { id: 'legal', label: 'Legal' },
  { id: 'automotive', label: 'Automotive' },
  { id: 'home_services', label: 'Home Services' },
  { id: 'other', label: 'Other' },
];

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const onboarding = useMemo(() => searchParams.get('onboarding') === '1', [searchParams]);

  const { user, fetchMe, updateMe } = useAuthStore();

  const [fullName, setFullName] = useState('');
  const [userType, setUserType] = useState<UserType>('individual');
  const [jobTitle, setJobTitle] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [teamSize, setTeamSize] = useState<string>('');
  const [searchCountry, setSearchCountry] = useState<string>('');
  const [searchCategory, setSearchCategory] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);

  // Billing / subscription state
  const [subscriptionTier, setSubscriptionTier] = useState<string>('free');
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [cancelSuccess, setCancelSuccess] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const me = await fetchMe();
        if (!mounted) return;
        setFullName(me.full_name || '');
        setUserType((me.user_type as UserType) || 'individual');
        setJobTitle(me.job_title || '');
        setCompanyName(me.company_name || '');
        setCompanyWebsite(me.company_website || '');
        setTeamSize(me.team_size != null ? String(me.team_size) : '');
        setSearchCountry(me.search_country || '');
        setSearchCategory(me.search_category || '');
      } catch (e) {
        // ignore; route guard will handle auth errors
      }
    })();
    return () => {
      mounted = false;
    };
  }, [fetchMe]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/subscription/status');
        if (!mounted) return;
        setSubscriptionTier(res.data?.tier || 'free');
      } catch {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaved(false);

    if (!fullName.trim()) {
      setError('Full name is required.');
      return;
    }
    if (userType === 'small_company' && !companyName.trim()) {
      setError('Company name is required for small company.');
      return;
    }
    if (onboarding && !searchCountry.trim()) {
      setError('Please select your search area (country).');
      return;
    }

    const parsedTeamSize =
      teamSize.trim() === '' ? null : Number.isFinite(Number(teamSize)) ? Number(teamSize) : NaN;
    if (typeof parsedTeamSize === 'number' && Number.isNaN(parsedTeamSize)) {
      setError('Team size must be a number.');
      return;
    }

    setLoading(true);
    try {
      await updateMe({
        full_name: fullName.trim(),
        user_type: userType,
        job_title: jobTitle.trim() || null,
        company_name: userType === 'small_company' ? companyName.trim() : null,
        company_website: userType === 'small_company' ? companyWebsite.trim() || null : null,
        team_size: userType === 'small_company' ? parsedTeamSize : null,
        search_country: searchCountry.trim() || null,
        search_category: (searchCategory.trim() || null) as SearchCategory | null,
      });
      setSaved(true);
      if (onboarding) {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to update profile'));
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalError('');
    setPortalLoading(true);
    try {
      const res = await api.post('/subscription/customer-portal');
      const url = res.data?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err: any) {
      setPortalError(getApiErrorMessage(err, 'Could not open billing portal'));
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Cancel your subscription? You will keep access until the end of your billing period.')) return;
    setCancelError('');
    setCancelSuccess(false);
    setCancelLoading(true);
    try {
      await api.post('/subscription/cancel');
      setCancelSuccess(true);
      setSubscriptionTier('free');
    } catch (err: any) {
      setCancelError(getApiErrorMessage(err, 'Could not cancel subscription'));
    } finally {
      setCancelLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSaved(false);

    if (newPassword !== confirmNewPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setPasswordLoading(true);
    try {
      await api.post('/auth/me/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPasswordSaved(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err: any) {
      setPasswordError(getApiErrorMessage(err, 'Failed to change password'));
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-indigo-50/30 to-slate-50">
      <Header />
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-10">
        {!onboarding && <BackToDashboard />}

        <div className="bg-white/90 backdrop-blur-sm rounded-3xl border border-slate-200/80 shadow-xl shadow-slate-200/50 overflow-hidden">
          {/* Header section with gradient */}
          <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-8 py-10">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-black text-white tracking-tight">Your Profile</h1>
                <p className="text-indigo-100 text-sm mt-0.5">
                  {onboarding ? 'Welcome! Tell us a bit about you.' : 'Update your details anytime.'}
                </p>
              </div>
            </div>
            {user?.email && (
              <div className="flex items-center gap-2 text-indigo-100/90 text-sm">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="font-medium">{user.email}</span>
              </div>
            )}
          </div>

          <div className="p-8">
            {error && (
              <div className="p-4 bg-rose-50 text-rose-700 rounded-xl mb-6 border border-rose-100 flex items-start gap-3">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}
            {saved && !onboarding && (
              <div className="p-4 bg-emerald-50 text-emerald-700 rounded-xl mb-6 border border-emerald-100 flex items-center gap-3">
                <svg className="w-5 h-5 flex-shrink-0 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Profile saved successfully.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Full name</label>
                <input
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">You are a...</label>
                <select
                  value={userType}
                  onChange={(e) => setUserType(e.target.value as UserType)}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all bg-white appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236366f1'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem',
                  }}
                >
                  <option value="individual">Individual</option>
                  <option value="freelancer">Freelancer</option>
                  <option value="small_company">Small company</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Search area (country) {onboarding && <span className="text-rose-500">*</span>}
                </label>
                <select
                  value={searchCountry}
                  onChange={(e) => setSearchCountry(e.target.value)}
                  required={onboarding}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all bg-white appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236366f1'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem',
                  }}
                >
                  <option value="">Select country</option>
                  {SEARCH_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {onboarding && (
                  <p className="text-xs text-slate-500 mt-1">Free plan: search within this country only. Upgrade for worldwide.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Business category <span className="font-normal text-slate-400">(optional)</span>
                </label>
                <select
                  value={searchCategory}
                  onChange={(e) => setSearchCategory(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all bg-white appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236366f1'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.75rem center',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem',
                  }}
                >
                  <option value="">Select category</option>
                  {SEARCH_CATEGORIES.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">Helps personalize your experience. Not required for search.</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Job title <span className="font-normal text-slate-400">(optional)</span></label>
                <input
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g. Growth Marketer"
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all"
                />
              </div>

              {userType === 'small_company' && (
                <div className="space-y-6 p-4 rounded-xl bg-slate-50/80 border border-slate-100">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Company name</label>
                    <input
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      required
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all"
                      placeholder="Your company name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Company website <span className="font-normal text-slate-400">(optional)</span></label>
                    <input
                      value={companyWebsite}
                      onChange={(e) => setCompanyWebsite(e.target.value)}
                      placeholder="https://example.com"
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Team size <span className="font-normal text-slate-400">(optional)</span></label>
                    <input
                      value={teamSize}
                      onChange={(e) => setTeamSize(e.target.value)}
                      placeholder="e.g. 5"
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all"
                    />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-lg shadow-indigo-500/25"
                >
                  {loading ? 'Saving...' : 'Save profile'}
                </button>
                {!onboarding && (
                  <Link
                    to="/dashboard"
                    className="px-6 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 transition-all"
                  >
                    Cancel
                  </Link>
                )}
              </div>
            </form>

            {!onboarding && (
              <div className="mt-12 pt-10 border-t border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Billing & subscription</h2>
                    <p className="text-slate-600 text-sm">
                      {subscriptionTier !== 'free' ? 'Manage your plan, payment method, and invoices.' : 'Upgrade for more searches and leads.'}
                    </p>
                  </div>
                </div>
                {portalError && <div className="p-4 bg-rose-50 text-rose-700 rounded-xl mb-4 border border-rose-100">{portalError}</div>}
                {cancelError && <div className="p-4 bg-rose-50 text-rose-700 rounded-xl mb-4 border border-rose-100">{cancelError}</div>}
                {cancelSuccess && (
                  <div className="p-4 bg-amber-50 text-amber-800 rounded-xl mb-4 border border-amber-200">
                    Subscription will be cancelled at the end of your billing period.
                  </div>
                )}
                <div className="flex flex-wrap gap-3">
                  {subscriptionTier !== 'free' ? (
                    <>
                      <button
                        type="button"
                        onClick={handleManageSubscription}
                        disabled={portalLoading}
                        className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60 transition-all"
                      >
                        {portalLoading ? 'Opening...' : 'Manage subscription'}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelSubscription}
                        disabled={cancelLoading}
                        className="px-6 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 disabled:opacity-60 transition-all"
                      >
                        {cancelLoading ? 'Cancelling...' : 'Cancel subscription'}
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/pricing"
                      className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all inline-block"
                    >
                      Upgrade plan
                    </Link>
                  )}
                </div>
              </div>
            )}

            {!onboarding && (
              <div className="mt-12 pt-10 border-t border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Change password</h2>
                    <p className="text-slate-600 text-sm">Update your password. Requires your current password.</p>
                  </div>
                </div>
                {passwordError && (
                  <div className="p-4 bg-rose-50 text-rose-700 rounded-xl mb-4 border border-rose-100">{passwordError}</div>
                )}
                {passwordSaved && (
                  <div className="p-4 bg-emerald-50 text-emerald-700 rounded-xl mb-4 border border-emerald-100 flex items-center gap-2">
                    <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Password changed successfully.
                  </div>
                )}
                <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Current password</label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">New password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Confirm new password</label>
                    <input
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      required
                      minLength={8}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all"
                    />
                  </div>
                  <p className="text-xs text-slate-500">At least 8 characters, one letter, one number, no spaces.</p>
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                  >
                    {passwordLoading ? 'Updating...' : 'Change password'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;

